import { FunctionComponent, memo, useContext, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import sha1 from 'sha1';
import { UserContext } from '../../../../context';
import { StoryCard, StoryCardAudioContent } from '../../../../services';
import { useAnalytics, useCardTitle } from '../../../hooks';
import { FileStatus } from '../../../models';
import { AudioPlayerTrigger } from '../../AudioPlayer';
import { ImageGallery, ImageGalleryItem } from '../../ImageGallery';
import { FeedCardFileError } from '../FeedCardFileError';
import { FeedCardMargin } from '../FeedCardMargin';

interface FeedCardAudioCommonProps {
  card: StoryCard;
  chatId?: number;
}

export const FeedCardAudioCommon: FunctionComponent<FeedCardAudioCommonProps> = memo(
  ({ card, chatId }) => {
    const { id: userId, channelId } = useContext(UserContext).userInfo.userData;

    const { logClickOpenArticle } = useAnalytics();

    const { id: cardId, storyId, content } = card;

    const cardTitle = useCardTitle(card);

    const { gallery } = content as StoryCardAudioContent;

    const galleryItem = useMemo(() => gallery[0], [gallery]);

    const { audio, title } = { ...galleryItem };

    const imageContent = useMemo(() => {
      switch (true) {
        case Boolean(gallery.find(({ image }) => image?.status === FileStatus.ERROR)):
          return (
            <FeedCardMargin top right bottom left>
              <FeedCardFileError />
            </FeedCardMargin>
          );
        case Boolean(gallery.find(({ image }) => image?.status === FileStatus.PROCESSING)):
          return (
            <FeedCardMargin top right bottom left>
              <Skeleton height={'5rem'} />
            </FeedCardMargin>
          );
        default: {
          const imageGalleryItems: ImageGalleryItem[] = gallery
            .map(({ image }) => {
              return {
                image: {
                  url: image?.url,
                  rightholder: image?.rightholder,
                  averageColor: image?.averageColor,
                },
              };
            })
            .filter(({ image }) => Boolean(image.url));

          return (
            <ImageGallery
              items={imageGalleryItems}
              modalPreviewOnClick
              onLogClickOpenArticle={() =>
                logClickOpenArticle({
                  user_Id: sha1(`${userId}`),
                  channel_id: channelId,
                  story_id: storyId,
                  item_id: cardId,
                  time_stamp: new Date().toISOString(),
                })
              }
            />
          );
        }
      }
    }, [cardId, channelId, gallery, logClickOpenArticle, storyId, userId]);

    const audioContent = useMemo(() => {
      switch (audio.status) {
        case FileStatus.ERROR:
          return (
            <FeedCardMargin top right bottom left>
              <FeedCardFileError />
            </FeedCardMargin>
          );
        case FileStatus.PROCESSING:
          return (
            <FeedCardMargin top right bottom left>
              <Skeleton height={'5rem'} />
            </FeedCardMargin>
          );
        default:
          return (
            <>
              {audio.url && (
                <FeedCardMargin top right left>
                  <AudioPlayerTrigger
                    storyId={storyId}
                    cardId={cardId}
                    cardTitle={cardTitle}
                    src={audio.url}
                    caption={title}
                    chatId={chatId}
                  />
                </FeedCardMargin>
              )}
            </>
          );
      }
    }, [audio.status, audio.url, cardId, cardTitle, chatId, storyId, title]);

    return (
      <>
        {imageContent}
        {audioContent}
      </>
    );
  }
);
